/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { SaleService } from '../services/sale-service';
import { salesKeys } from './sales-keys';
import type { SaleState } from '../constants/sale-states';
import type {
  CommissionReceipt,
  Sale,
  SaleCommissionPanel,
  SaleDocument,
} from '../types/sale.types';

export interface SaleDetail {
  isLoading: boolean;
  isError: boolean;
  sale: Sale | null;
  commission: SaleCommissionPanel | null;
  documents: SaleDocument[];
  applyTransition: (target: SaleState, reason?: string) => Promise<void>;
  recordPaperwork: (dates: Record<string, string>) => Promise<void>;
  recordReceipt: (receipt: CommissionReceipt) => Promise<void>;
  refresh: () => void;
}

/**
 * Loads one sale record with its derived commission panel and documents,
 * and exposes the three acts that change it.
 *
 * The commission panel is fetched rather than computed here: the backend
 * derives it, and a second implementation in the client would be a second
 * thing to keep in agreement.
 *
 * @param agencyId - The owning agency.
 * @param saleId - The sale to load.
 * @returns The view-model: flags, the record, its panel, docs and actions.
 */
export function useSale(agencyId: string, saleId: string): SaleDetail {
  const queryClient = useQueryClient();
  const enabled = Boolean(agencyId && saleId);

  const saleQuery = useQuery({
    queryKey: salesKeys.detail(agencyId, saleId),
    queryFn: () => SaleService.getSale(agencyId, saleId),
    enabled,
  });

  const commissionQuery = useQuery({
    queryKey: salesKeys.commission(agencyId, saleId),
    queryFn: () => SaleService.getCommissionPanel(agencyId, saleId),
    enabled,
  });

  const documentsQuery = useQuery({
    queryKey: salesKeys.documents(agencyId, saleId),
    queryFn: () => SaleService.listDocuments(agencyId, saleId),
    enabled,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['sales', agencyId] });

  return {
    isLoading: saleQuery.isLoading,
    isError: saleQuery.isError,
    sale: saleQuery.data ?? null,
    commission: commissionQuery.data ?? null,
    documents: documentsQuery.data?.data ?? [],
    applyTransition: async (target, reason) => {
      await SaleService.applyTransition(agencyId, saleId, target, reason);
      refresh();
    },
    recordPaperwork: async (dates) => {
      await SaleService.recordPaperwork(agencyId, saleId, dates);
      refresh();
    },
    recordReceipt: async (receipt) => {
      await SaleService.recordCommissionReceipt(agencyId, saleId, receipt);
      refresh();
    },
    refresh,
  };
}
